import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from '@material-ui/core';
import {
    getBluedartDeactivationReasons,
    raiseBluedartDeactivation,
} from '../../store/actions/staffActionCreator';
import { showMessage } from '../../utils/helper';
import RehireIneligibleConfirmDialog from './RehireIneligibleConfirmDialog';

interface RiderDeactivationModalProps {
    open: boolean;
    rider: any;
    onClose: () => void;
    onSuccess: () => void;
}

/** "13 Oct 2026" — the form talks in dates a person reads, the API in yyyy-MM-dd. */
const displayDate = (iso?: string | null): string =>
    iso
        ? new Date(iso).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
          })
        : '';

/**
 * Raises a deactivation request (PRD DR-2, DR-3).
 *
 * <p>This does NOT deactivate the rider. It creates a request that a Regional Approver or Super Admin
 * decides, and the rider stays operational until then — the copy says so explicitly, because an
 * approver assuming it took effect is the obvious failure here.
 *
 * <p>The last working date is the rider's real final day, which Blue Dart records as the
 * resignation date. A date still to come does not deactivate them early: they keep working, and
 * the separation lands once that day has ended.
 */
const RiderDeactivationModal: React.FC<RiderDeactivationModalProps> = ({
    open,
    rider,
    onClose,
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const [reasons, setReasons] = useState<any[]>([]);
    const [reasonCode, setReasonCode] = useState('');
    const [remark, setRemark] = useState('');
    const [lastWorkingDate, setLastWorkingDate] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    // The window and both toggles come from the server rather than being repeated here, so a
    // change to the config cannot leave the picker offering dates the API will refuse.
    const [dateRules, setDateRules] = useState<any>({});

    useEffect(() => {
        dispatch(
            getBluedartDeactivationReasons(
                (response: any) => {
                    const body = response?.response ?? {};
                    setReasons(body.reasons ?? []);
                    setDateRules(body);
                },
                () => {
                    setReasons([]);
                    setDateRules({});
                },
            ),
        );
    }, [dispatch]);

    // The modal stays mounted between riders, so without this one rider's reason and final day
    // would be waiting in the form for the next.
    useEffect(() => {
        if (open) {
            setReasonCode('');
            setRemark('');
            setLastWorkingDate('');
            setConfirmOpen(false);
        }
    }, [open, rider?.id]);

    const selected = reasons.find((r: any) => r.code === reasonCode);
    const barsRejoining = selected?.rehireEligible === 'NO';

    const dateRequired = dateRules?.lastWorkingDateRequired !== false;
    const minDate = dateRules?.minLastWorkingDate;
    const maxDate = dateRules?.maxLastWorkingDate;

    // Out of range is reachable by typing even with min/max set on the input, so it is spelled out
    // rather than left to the browser to refuse silently.
    const dateError = useMemo(() => {
        if (!dateRequired || !lastWorkingDate) return '';
        if (
            (minDate && lastWorkingDate < minDate) ||
            (maxDate && lastWorkingDate > maxDate)
        ) {
            return `Last Working Date must be between ${displayDate(
                minDate,
            )} and ${displayDate(maxDate)}.`;
        }
        return '';
    }, [dateRequired, lastWorkingDate, minDate, maxDate]);

    const submitDisabled =
        !reasonCode ||
        submitting ||
        (dateRequired && !lastWorkingDate) ||
        !!dateError;

    const submit = () => {
        setConfirmOpen(false);
        setSubmitting(true);
        dispatch(
            raiseBluedartDeactivation(
                rider?.id,
                {
                    reasonCode,
                    remark,
                    lastWorkingDate: lastWorkingDate || null,
                    rehireIneligibleConfirmed: barsRejoining,
                },
                (response: any) => {
                    setSubmitting(false);
                    showMessage(
                        response?.status?.message ??
                            'Deactivation request submitted',
                        'success',
                    );
                    onSuccess();
                    onClose();
                },
                (error: any) => {
                    setSubmitting(false);
                    showMessage(
                        error?.status?.message ??
                            'Could not raise the deactivation request',
                        'error',
                    );
                },
            ),
        );
    };

    const handleSubmit = () => {
        if (
            barsRejoining &&
            dateRules?.rehireIneligibleConfirmationRequired !== false
        ) {
            setConfirmOpen(true);
            return;
        }
        submit();
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
                <DialogTitle>Deactivate {rider?.name}</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText style={{ fontSize: 14 }}>
                        This raises a request for approval. The rider remains
                        active until it is approved.
                    </DialogContentText>

                    <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Reason *"
                        value={reasonCode}
                        onChange={(e) => setReasonCode(e.target.value)}
                        style={{ marginTop: 8, marginBottom: 8 }}
                    >
                        {reasons.map((reason: any) => (
                            <MenuItem key={reason.code} value={reason.code}>
                                {reason.code} — {reason.description}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* The reason decides whether this rider can ever come back, so it is stated
                        before the decision rather than discovered at reactivation time. */}
                    {selected && selected.rehireEligible !== 'YES' && (
                        <Typography
                            variant="body2"
                            color="error"
                            style={{ marginBottom: 8 }}
                        >
                            {selected.rehireEligible === 'NA'
                                ? 'Reactivation will be permanently disabled for this rider.'
                                : 'This rider will not be eligible for rehire.'}
                        </Typography>
                    )}

                    {dateRequired && (
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            variant="outlined"
                            label="Last Working Date *"
                            value={lastWorkingDate}
                            onChange={(e) => setLastWorkingDate(e.target.value)}
                            error={!!dateError}
                            helperText={
                                dateError ||
                                'The rider works until the end of this day. A date still to come keeps them active until then.'
                            }
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: minDate, max: maxDate }}
                            style={{ marginBottom: 8 }}
                        />
                    )}

                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        variant="outlined"
                        label="Remark (optional)"
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        disabled={submitDisabled}
                        onClick={handleSubmit}
                    >
                        {submitting ? (
                            <CircularProgress size={18} />
                        ) : (
                            'Submit request'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <RehireIneligibleConfirmDialog
                open={confirmOpen}
                reason={selected?.description}
                confirmLabel="Confirm and submit"
                onGoBack={() => setConfirmOpen(false)}
                onConfirm={submit}
            />
        </>
    );
};

export default RiderDeactivationModal;
